import { type JSX, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createEngine, type Engine } from '@runtime/engine.js';
import type { LoadFailure } from '@runtime/loader.js';
import type { RenderTarget } from '@runtime/render-target.js';
import { LocalStorageKeyValueStore } from '@runtime/storage.js';
import type { AdminAccess } from '@schema/admin.js';
import type { FeatureFlag } from '@schema/features.js';
import type { Profile } from '@schema/profile.js';
import type { Tile } from '@schema/tiles.js';

import type { AgentHost } from '@runtime/agent.js';
import type { MobiLauncherConfig } from '@schema/index.js';
import type { Mdm } from '@schema/mdm.js';

import { AndroidIntentAgentHost, hasAndroidBridge } from './android-intent-agent-host.js';
import {
  advanceClockFloor,
  fetchConfigWithCache,
  fetchLicenseTextWithCache,
  fetchRevocationList,
  licenseIdFromText,
  verifyBundleIntegrity,
  verifyLicenseText,
} from './boot.js';
import {
  apiBaseUrlFromConfigUrl,
  getDeviceIdentity,
  registerDevice,
} from './bridge/device.js';
import { BrowserAgentHost } from './browser-agent-host.js';
import { createDetectingOemLoader, probeFromAgentHost } from './oem/detecting-loader.js';
import { friendlyDemoLabel, showDemoToast } from './demo-toast.js';
import {
  ConfigErrorScreen,
  type ConfigErrorVariant,
} from './components/ConfigErrorScreen.js';
import { ProfileView } from './components/ProfileView.js';
import {
  configNeedsPilotContext,
  fetchPilotContext,
  type FetchedPilotContext,
} from './pilot-context.js';
import { applyPilotFilter } from './pilot-filter.js';
import { type RevocationWatch, useRevocationRefresh } from './revocation-refresh.js';
import { useSignageRotation } from './hooks/use-signage-rotation.js';
import { hasSotiAgent, SotiAgentHost } from './soti-agent-host.js';
import { resolveConfigUrl, resolveLicenseUrl } from './tenant.js';

const ProFeatures = lazy(async () => ({
  default: (await import('./pro/index.js')).ProFeatures,
}));

interface EngineHandle {
  engine: Engine;
  adminAccess: AdminAccess;
}

type View =
  | { kind: 'loading' }
  | { kind: 'profile'; profile: Profile; features: ReadonlySet<FeatureFlag> }
  | { kind: 'configError'; failure: LoadFailure };

export interface AppProps {
  /**
   * Source URL for the config JSON. Defaults to the value of
   * `resolveConfigUrl()`, which reads the current URL for a tenant id
   * (path `/t/:id` or query `?tenant=:id`) and resolves against either
   * `VITE_CONFIG_URL_TEMPLATE` or sibling `./configs/<id>.json`. Tests
   * override directly.
   */
  configUrl?: string;
  /**
   * Source URL for the license JSON. Same resolution rules as configUrl
   * via `resolveLicenseUrl()`. Defaults to `./license.json` when no
   * tenant is present (demo / dev path).
   */
  licenseUrl?: string;
  /**
   * Explicit tenant id for broadcasts polling. Resolved automatically
   * from the URL on production launcher pages (`/t/:tenantId`), but the
   * marketing-site preview at `/` has no tenant in its URL and needs
   * the caller to supply one. When omitted, broadcasts polling is
   * URL-derived (and turned off entirely when no tenant is present).
   */
  tenantIdOverride?: string;
}

/**
 * Top-level launcher component. On mount: fetches config, creates an
 * engine, and connects the engine's RenderTarget to React state. Profile
 * transitions and config-error states flow through `view`. Pro-tier
 * features (exit gesture, diagnostic overlay) lazy-load via dynamic import
 * so Core devices never download or parse them.
 */
export function App({
  configUrl = resolveConfigUrl(),
  licenseUrl = resolveLicenseUrl(),
  tenantIdOverride,
}: AppProps = {}): JSX.Element {
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [engineHandle, setEngineHandle] = useState<EngineHandle | null>(null);
  const [bootKey, setBootKey] = useState(0);
  const engineRef = useRef<Engine | null>(null);
  /**
   * Full parsed config kept as state so the signage-rotation hook
   * below can re-run when it changes. The engine internally owns
   * the config but doesn't expose it on EngineHandle; tracking it
   * here avoids widening the public Engine surface for this.
   */
  const [config, setConfig] = useState<MobiLauncherConfig | null>(null);
  // Stable device identity for render-time pilot targeting. getDeviceIdentity
  // is idempotent (persists/returns the same UUID), so memoizing once is safe
  // and matches the identity the boot effect registers with.
  const deviceIdentity = useMemo(() => getDeviceIdentity(), []);
  // Server-supplied device context (public IP + tags) for subnet/tag pilot
  // rules. Null until fetched — those rules fail closed in the meantime.
  const [pilotContext, setPilotContext] = useState<FetchedPilotContext | null>(null);
  // What the mid-session revocation poll watches: the running license's id +
  // its revocation endpoint. Null when there's no license id to watch (legacy
  // license, Core, or no API base). Set by the boot effect after verification.
  const [revocationWatch, setRevocationWatch] = useState<RevocationWatch | null>(null);
  // Shared store for the revocation poll — same localStorage keys as the boot
  // effect's store, so the cached list is unified. Stable across renders.
  const revocationStore = useMemo(() => new LocalStorageKeyValueStore(), []);

  useEffect(() => {
    let cancelled = false;
    let engine: Engine | null = null;

    const renderTarget: RenderTarget = {
      renderProfile: (input) => {
        if (cancelled) return;
        setView({ kind: 'profile', profile: input.profile, features: input.features });
      },
      showConfigError: (input) => {
        if (cancelled) return;
        setView({
          kind: 'configError',
          failure: { kind: 'parseJson', message: input.message },
        });
      },
    };

    // Resolve the device identity synchronously up front so the very
    // first config fetch can carry `?deviceId=`. The server bumps
    // last_seen_at on every config call, so the Portal's Devices panel
    // shows fresh "active" timestamps without a separate heartbeat.
    const identity = getDeviceIdentity();
    const configUrlWithDevice = appendDeviceQuery(configUrl, identity.deviceId);
    // Last-known-good cache so a kiosk rebooting offline still comes up.
    const bootStore = new LocalStorageKeyValueStore();

    void (async () => {
      const [configLoad, licenseText, bundleVerify] = await Promise.all([
        // Live config, falling back to last-known-good when the server is
        // unreachable (network/httpError) so an offline reboot isn't stranded.
        fetchConfigWithCache(configUrlWithDevice, bootStore),
        // Raw license in parallel (cached fallback for offline); verified below
        // once we know the tenant. null is the "run as Core" signal.
        fetchLicenseTextWithCache(licenseUrl, bootStore),
        // Signed-bundle integrity gate. No-op ('absent') for hosted/demo/
        // unsigned bundles; hard-fails the boot when a present manifest
        // doesn't verify (security: refuse a tampered config/license).
        verifyBundleIntegrity(configUrl),
      ]);
      const configResult = configLoad.result;
      if (cancelled) return;
      if (bundleVerify.kind === 'failed') {
        setView({
          kind: 'configError',
          failure: { kind: 'bundleTamper', message: bundleVerify.message },
        });
        return;
      }
      if (!configResult.ok) {
        setView({ kind: 'configError', failure: configResult.failure });
        return;
      }
      setConfig(configResult.value);
      // API base derived from the config URL (so a custom VITE_CONFIG_URL_
      // TEMPLATE works without extra env vars). Reused for the revocation list,
      // device registration, and the health heartbeat below.
      const apiBase = apiBaseUrlFromConfigUrl(configUrl);
      // Advance the monotonic clock high-water-mark and use it as the
      // anti-rollback floor for license expiry — winding the device clock back
      // can't un-expire a license.
      const clockFloor = advanceClockFloor(bootStore, Math.floor(Date.now() / 1000));
      // Fetch the signed revocation list (fail-open, cached) so a specifically-
      // revoked license.json demotes the device to Core even offline.
      const revocationUrl = apiBase
        ? `${apiBase}/licenses/revocations/by-tenant/${configResult.value.tenantId}`
        : null;
      const revokedIds = revocationUrl
        ? await fetchRevocationList(revocationUrl, configResult.value.tenantId, bootStore)
        : new Set<string>();
      if (cancelled) return;
      // Verify the license now that we know the tenant the config bound to —
      // signature + tenant + expiry (grace + rollback aware) + revocation all
      // checked; null → run as Core.
      const license = await verifyLicenseText(
        licenseText,
        configResult.value.tenantId,
        clockFloor,
        revokedIds,
      );
      if (cancelled) return;
      // Arm the mid-session revocation poll only when there's a specific license
      // id to watch (an id-bearing license that verified, with a reachable API).
      setRevocationWatch(
        license?.id !== undefined && revocationUrl !== null
          ? { licenseId: license.id, url: revocationUrl, tenantId: configResult.value.tenantId }
          : null,
      );
      // License status for the heartbeat: a rejected-but-present license
      // ('invalid') is the silent-downgrade case the fleet view needs — the
      // engine only sees the applied license (null), not that a text existed.
      const licenseStatus: 'valid' | 'invalid' | 'none' =
        license !== null ? 'valid' : licenseText !== null ? 'invalid' : 'none';
      // The license id to report. For a valid license it's the trusted id; for
      // an INVALID one (verify returned null — the case admins most want to
      // trace) we best-effort read the id from the raw text, since that's what
      // the revocation list is keyed on. Untrusted, but the id is only a label.
      const licenseId = license?.id ?? licenseIdFromText(licenseText);
      const agentHost = selectAgentHost(configResult.value.mdm);
      // Push the config's app-tile packages to the host's lock-task allowlist so
      // a pinned (kiosk) device stays pinned when a tile launches another app.
      // Capability-detected: hosts without a kiosk backend (browser, SOTI,
      // un-enrolled) omit setAllowedPackages and this is a no-op. Re-run on
      // every remote-config swap so newly added app tiles don't drop pinning.
      const syncAndroidAllowlist = (cfg: MobiLauncherConfig): void => {
        void agentHost.setAllowedPackages?.(collectAppPackageNames(cfg)).catch(() => {
          // Older shell without the bridge method, or a DPM hiccup — non-fatal.
        });
      };
      engine = createEngine({
        config: configResult.value,
        license,
        renderTarget,
        agentHost,
        // When a remote-config poll swaps in a new config, sync the state the
        // renderer derives from the raw config outside the engine's render
        // path: the signage-rotation source and the Android lock-task
        // allowlist. The engine already repaints the active profile itself.
        onConfigChanged: (cfg) => {
          if (cancelled) return;
          setConfig(cfg);
          syncAndroidAllowlist(cfg);
        },
        // SDK-free auto-detection: identifies the vendor from device
        // system properties / UA and activates the matching (no-op)
        // module. Forced detection (`device.detection = 'forceZebra'`
        // etc.) still works. Real per-OEM SDK integrations land per
        // beta partner; see oem/detecting-loader.ts.
        oemModuleLoader: createDetectingOemLoader(probeFromAgentHost(agentHost)),
        // Lets the Portal's Devices panel join telemetry events to
        // registered devices: every flushed event carries
        // attrs.deviceId on the wire.
        deviceId: identity.deviceId,
        // Device-health heartbeat (all tiers) — periodic liveness + health to
        // the Portal's Devices panel. Only when we can address the API.
        ...(apiBase ? { heartbeatEndpoint: `${apiBase}/devices/heartbeat` } : {}),
        ...(identity.appVersion !== null ? { appVersion: identity.appVersion } : {}),
        licenseStatus,
        ...(licenseId !== null ? { licenseId } : {}),
      });
      engineRef.current = engine;
      try {
        await engine.start();
        if (!cancelled) {
          syncAndroidAllowlist(configResult.value);
          setEngineHandle({ engine, adminAccess: configResult.value.adminAccess });

          // Fire-and-forget device registration (apiBase computed above).
          // Failures are swallowed inside registerDevice — never blocks boot.
          if (apiBase) {
            void registerDevice({
              apiBaseUrl: apiBase,
              tenantId: tenantIdOverride ?? configResult.value.tenantId,
              identity,
            });
            // Only fetch pilot context when a subnet/tag pilot is actually
            // configured — percentage/ramp need nothing from the server.
            if (configNeedsPilotContext(configResult.value)) {
              void fetchPilotContext(
                apiBase,
                identity.deviceId,
                tenantIdOverride ?? configResult.value.tenantId,
              ).then((ctx) => {
                if (!cancelled && ctx) setPilotContext(ctx);
              });
            }
          }
        }
      } catch (err) {
        if (cancelled) return;
        setView({
          kind: 'configError',
          failure: {
            kind: 'parseJson',
            message: err instanceof Error ? err.message : String(err),
          },
        });
      }
    })();

    return () => {
      cancelled = true;
      void engine?.stop();
      engineRef.current = null;
      setEngineHandle(null);
    };
  }, [configUrl, bootKey]);

  // Signage auto-rotation — when entitled + configured, cycles through
  // profiles[] at the configured interval. See use-signage-rotation.ts
  // for the full contract; same hook backs the Builder preview.
  const engineEmittedProfileId =
    view.kind === 'profile' ? view.profile.id : null;
  const signageEntitled =
    view.kind === 'profile' && view.features.has('signageMode');
  const rotatedProfile = useSignageRotation(config, engineEmittedProfileId, signageEntitled);

  const handleTileActivate = useCallback(async (tile: Tile): Promise<void> => {
    const engine = engineRef.current;
    if (!engine) return;
    const result = await engine.dispatchTile(tile);
    if (!result.ok) {
      // In browser-only mode (marketing demo, no Android bridge),
      // app/agentAction tiles always "fail" because BrowserAgentHost
      // can't launch native apps. Show a friendly toast using the
      // tile's customer-set label instead of the raw "requires SOTI"
      // error, so the demo feels responsive.
      if (result.reason === 'agentError' && !hasAndroidBridge()) {
        showDemoToast(friendlyDemoLabel(tile));
        return;
      }
      // Surface verbose bridge errors to the console for ops
      // diagnostics (chrome://inspect on the kiosk shows them) while
      // the tile-side status hint stays short + friendly.
      console.warn('[MobiLauncher] tile dispatch failed:', result);
      throw new Error(describeFailure(result));
    }
  }, []);

  const handleRetry = useCallback(() => {
    setView({ kind: 'loading' });
    setEngineHandle(null);
    setBootKey((k) => k + 1);
  }, []);

  // Re-boot when this device's own license is revoked mid-session — a fresh
  // boot re-verifies against the updated revocation list and drops to Core.
  const handleRevoked = useCallback(() => {
    setRevocationWatch(null); // stop watching; the re-boot will re-arm if licensed
    setView({ kind: 'loading' });
    setEngineHandle(null);
    setBootKey((k) => k + 1);
  }, []);
  useRevocationRefresh({ watch: revocationWatch, store: revocationStore, onRevoked: handleRevoked });

  if (view.kind === 'loading') {
    return (
      <div className="loading" data-testid="loading">
        Loading…
      </div>
    );
  }
  if (view.kind === 'configError') {
    const variant = variantForFailure(view.failure);
    return (
      <ConfigErrorScreen
        variant={variant}
        details={view.failure}
        onRetry={handleRetry}
        // Auto-retry offline/server-error so unattended kiosks recover
        // when the network/server returns. Schema + crash variants
        // require a human (admin republish or app restart), so no
        // auto-retry there — clicking forever would just thrash.
        {...(variant === 'offline' && { autoRetrySeconds: 15 })}
        {...(variant === 'serverError' && { autoRetrySeconds: 30 })}
      />
    );
  }

  const showProFeatures = engineHandle !== null && engineHandle.engine.tier !== 'core';

  // When signage rotation is active, swap in the rotated profile. The
  // engine still owns "what the device would be on without rotation"
  // via view.profile; we layer rotation on top here at render time.
  // Then drop any pilot tiles this device isn't targeted for (no-op unless
  // the tenant is entitled to pilotRollout). Phase A resolves percentage +
  // ramp from the deviceId alone; subnet/tag context lands in a later phase.
  const activeProfile = applyPilotFilter(
    rotatedProfile ?? view.profile,
    {
      deviceId: deviceIdentity.deviceId,
      // Subnet rules match the device's OWN reported LAN IPv4 — no server
      // round-trip. Tags come from the lazy pilot-context fetch.
      ...(deviceIdentity.localIpv4 !== null && { lanIpv4: deviceIdentity.localIpv4 }),
      ...(pilotContext ?? {}),
    },
    view.features.has('pilotRollout'),
    Date.now(),
  );

  return (
    <>
      <ProfileView
        profile={activeProfile}
        features={view.features}
        onTileActivate={handleTileActivate}
        onRefresh={() => window.location.reload()}
        {...(engineHandle && { engine: engineHandle.engine })}
        {...(tenantIdOverride !== undefined && { tenantIdOverride })}
      />
      {showProFeatures && engineHandle !== null && (
        <Suspense fallback={null}>
          <ProFeatures engine={engineHandle.engine} adminAccess={engineHandle.adminAccess} />
        </Suspense>
      )}
    </>
  );
}

/**
 * Pick the right AgentHost for this deployment + runtime context. The launcher
 * adapts to whichever native bridge is actually injected; the `mdm` value only
 * gates the SOTI adapter (an explicit opt-in), so a stale config never strands
 * a device on the wrong host.
 *
 *   1. SOTI MobiControl host — ONE adapter, used only when the operator opted
 *      into MobiControl (`mdm==='mobicontrol'`) AND the SOTI JS Agent globals
 *      are injected. SOTI then backs the agent-action tiles + MDM-only Device
 *      Info fields. It is no longer the foundation — just the adapter for SOTI
 *      deployments.
 *   2. Native Android shell — the deployment BACKBONE. Our shell's
 *      JavaScriptInterface bridge means we're inside the Android Enterprise /
 *      AMAPI-managed HOME + lock-task kiosk. This is the default path for a
 *      dedicated-device fleet (an AE-native deployment isn't `mobicontrol`, so
 *      it lands here) and needs no third-party MDM.
 *   3. Browser host — everything else (marketing demo, dev server,
 *      browser-only sandbox). Agent-tile actions show friendly demo toasts.
 *
 * SOTI and the native shell are mutually exclusive at runtime (a SOTI Lockdown
 * WebView has no native bridge and vice-versa), so the order only settles the
 * contrived both-present case in favor of the explicit SOTI opt-in.
 */
export function selectAgentHost(mdm: Mdm): AgentHost {
  if (mdm === 'mobicontrol' && hasSotiAgent()) return new SotiAgentHost();
  if (hasAndroidBridge()) return new AndroidIntentAgentHost();
  return new BrowserAgentHost();
}

function describeFailure(result: { ok: false } & Record<string, unknown>): string {
  const reason = result['reason'];
  if (reason === 'notEntitled') return `Feature locked: ${String(result['flag'])}`;
  if (reason === 'agentError') return friendlyAgentError(String(result['message']));
  if (reason === 'unsupportedTileType') return `Unsupported tile type: ${String(result['type'])}`;
  return 'Unknown failure';
}

/**
 * Translate the bridge's verbose error envelope into a short
 * user-facing string. Pattern-matches the common cases we know the
 * native AndroidBridge / SOTI host emit; falls back to a generic
 * "Couldn't open" when the source isn't recognized.
 *
 * The original raw message is logged to the console via the
 * `console.warn` in handleTileActivate so ops still has the full
 * detail for diagnosis via chrome://inspect.
 */
function friendlyAgentError(raw: string): string {
  // Bridge says the package isn't installed (truly missing) OR is
  // hidden by Android 11+ package-visibility (the launcher's
  // <queries> doesn't cover it). Either way the user just needs
  // to know the app isn't there.
  if (/package not installed|package-visibility|hidden by Android/i.test(raw)) {
    return 'App not installed';
  }
  // MDM-only operations on a non-MDM host (reboot, clearAppData,
  // runScript, syncNow on AndroidIntentAgentHost or BrowserAgentHost).
  if (/requires.*(SOTI|MDM|system privilege|managed)/i.test(raw)) {
    return 'Not available on this device';
  }
  // Generic browser-host failure surfaced for fallback paths.
  if (/network/i.test(raw)) {
    return 'Network error';
  }
  // Anything else — short, generic, doesn't expose internals.
  return "Couldn't open";
}

/**
 * Walk the loaded config and collect every `type: 'app'` tile's
 * packageName across every profile. Used to push the lock-task
 * allowlist to the native bridge on boot — without this, launching
 * an app tile in device-owner-enrolled mode drops pinning.
 */
function collectAppPackageNames(config: MobiLauncherConfig): string[] {
  const seen = new Set<string>();
  for (const profile of config.profiles) {
    for (const tile of profile.grid.tiles) {
      if (tile.type === 'app') {
        seen.add(tile.packageName);
      }
    }
    for (const tile of profile.tools ?? []) {
      if (tile.type === 'app') {
        seen.add(tile.packageName);
      }
    }
  }
  return [...seen];
}

/**
 * Append `?deviceId=` to a config URL, preserving any existing query
 * string and respecting whether the URL is absolute or relative. We
 * use string concatenation rather than `new URL(...)` for the relative
 * case — `new URL('./config.json', window.location.href)` mangles paths
 * served from sub-directories.
 */
function appendDeviceQuery(url: string, deviceId: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}deviceId=${encodeURIComponent(deviceId)}`;
}

function variantForFailure(failure: LoadFailure): ConfigErrorVariant {
  switch (failure.kind) {
    case 'network':
      // Even when the offline flag is false (DNS / CORS / TLS), the
      // user-facing story is the same: we can't reach the server.
      return 'offline';
    case 'httpError':
      // 5xx → server-side, 4xx → config addressing (bad tenant, etc.),
      // both manifest the same way to the customer: server isn't
      // serving a usable config. Treat as serverError; the JSON status
      // is in the expandable details for support.
      return 'serverError';
    case 'bundleTamper':
      return 'tampered';
    case 'parseJson':
    case 'schema':
    case 'assetHashMismatch':
    case 'licenseInvalid':
      return 'invalidConfig';
  }
}
